import { useCallback, useEffect, useRef, useState } from 'react';
import { startSecondaryUpload as defaultStartSecondaryUpload } from '../api/secondaryUpload';
import { RETRIABLE_SECONDARY_STATUSES } from '../domain/constants';
import {
  applyCaveStateToFileEntry,
  secondaryUploadEntriesEqual,
} from '../domain/fileTracking';

export const useSecondaryUpload = ({
  formData,
  onChange,
  caveEnabled,
  startUpload = defaultStartSecondaryUpload,
  retriableStatuses = RETRIABLE_SECONDARY_STATUSES,
}) => {
  const [secondaryUploads, setSecondaryUploads] = useState({});
  const secondaryStatusRef = useRef({});

  const setSecondaryUpload = useCallback((trackingKey, payload) => {
    if (!trackingKey) {
      return;
    }

    setSecondaryUploads(prev => {
      const prevEntry = prev[trackingKey] || {};
      const nextEntry = { ...prevEntry, ...payload };

      if (secondaryUploadEntriesEqual(prevEntry, nextEntry)) {
        return prev;
      }

      return {
        ...prev,
        [trackingKey]: nextEntry,
      };
    });
  }, []);

  const clearSecondaryTrackingState = useCallback(trackingKey => {
    if (!trackingKey) {
      return;
    }

    setSecondaryUploads(prev => {
      if (!prev[trackingKey]) {
        return prev;
      }
      const next = { ...prev };
      delete next[trackingKey];
      return next;
    });

    delete secondaryStatusRef.current[trackingKey];
  }, []);

  const startOrReuseSecondaryUpload = useCallback(
    (file, trackingKey) => {
      if (!caveEnabled || !file || !trackingKey) {
        return;
      }

      const metadata = {
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
      };
      const existingSecondary = secondaryUploads[trackingKey];

      if (
        !existingSecondary ||
        retriableStatuses.has(existingSecondary.status)
      ) {
        startUpload(file, trackingKey, {
          statusRef: secondaryStatusRef,
          existingUploads: secondaryUploads,
          onUpdate: setSecondaryUpload,
        });
      } else {
        secondaryStatusRef.current[trackingKey] = existingSecondary.status;
        setSecondaryUpload(trackingKey, {
          status: existingSecondary.status,
          ...metadata,
        });
      }
    },
    [
      caveEnabled,
      retriableStatuses,
      secondaryUploads,
      setSecondaryUpload,
      startUpload,
    ],
  );

  useEffect(() => {
    const files = formData || [];
    const usedKeys = new Set();
    let changed = false;

    const next = files.map(entry => {
      const existingKey = entry?.idpTrackingKey;
      let info = existingKey ? secondaryUploads[existingKey] : null;
      let resolvedKey = existingKey;

      if (!info) {
        const match = Object.entries(secondaryUploads).find(
          ([key, value]) =>
            !usedKeys.has(key) &&
            value?.name === entry?.name &&
            value?.size === entry?.size &&
            value?.lastModified === entry?.lastModified,
        );
        if (match) {
          [resolvedKey, info] = match;
        }
      }

      if (!info) {
        return entry;
      }

      usedKeys.add(resolvedKey);

      const baseEntry =
        resolvedKey && resolvedKey !== existingKey
          ? { ...entry, idpTrackingKey: resolvedKey }
          : entry;

      const merged = applyCaveStateToFileEntry(baseEntry, info);
      if (merged !== baseEntry) {
        changed = true;
      }

      return merged;
    });

    if (changed) {
      onChange(next);
    }
  }, [secondaryUploads, formData, onChange]);

  return {
    secondaryUploads,
    clearSecondaryTrackingState,
    startOrReuseSecondaryUpload,
  };
};
